import type {
  OperationalLoopConstraint,
  OperationalLoopReport,
  OperationalLoopSeries,
  OperationalLoopWindow,
} from "@/lib/api/building";
import { PERSISTED_NAME_FOR_ZONE_KPI } from "@/lib/zone-kpi";
import {
  HELSINKI_TIME_ZONE,
  type OperationalLoopPoint,
} from "./operational-loop-data";
import { SIGNAL_LABELS } from "./verification";

// Converts the API's generic named-series shape into the specific per-metric fields the chart
// components already expect. Pure reshaping — no bucketing, no gap-filling, no anchor math: the
// backend already did all of that (services/building/.../domain/services/operational_loop.py).
// The only real work left here is a display-convention unit choice (EUR/kWh -> snt/kWh,
// on/off -> 0/100) — the same kind of call a chart's axis label or color is, not business logic.

const EMPTY_POINT_FIELDS: Omit<
  OperationalLoopPoint,
  "key" | "time" | "isHourMark" | "isNow" | "isPast"
> = {
  spotPriceCtPerKwh: null,
  outdoorTempC: null,
  solarRadiationWm2: null,
  occupancyIndex: null,
  occupancyHeadcount: null,
  occupancyPresence: null,
  co2Index: null,
  zoneTemperatureC: null,
  fanPowerBaselinePct: null,
  fanPowerOptimizedPct: null,
  supplyTempBaselineC: null,
  supplyTempOptimizedC: null,
  fanPowerMeasuredPct: null,
  supplyTempMeasuredC: null,
  elecKw: null,
  heatKw: null,
  totalOptimizedKw: null,
  totalBaselineKw: null,
  elecKwh: null,
  totalBaselineKwh: null,
};

function seriesToPoints(
  window: OperationalLoopWindow,
  series: OperationalLoopSeries[],
  applyValue: (
    point: OperationalLoopPoint,
    s: OperationalLoopSeries,
    value: number,
  ) => void,
): OperationalLoopPoint[] {
  const anchorMs = new Date(window.anchor).getTime();
  const stepMs = window.step_minutes * 60_000;
  const totalSteps = window.past_steps + window.future_steps + 1;

  const points: OperationalLoopPoint[] = [];
  for (let i = 0; i < totalSteps; i++) {
    const date = new Date(anchorMs + i * stepMs);
    const point: OperationalLoopPoint = {
      ...EMPTY_POINT_FIELDS,
      key: i,
      time: date.toLocaleTimeString("fi-FI", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: HELSINKI_TIME_ZONE,
      }),
      isHourMark: date.getMinutes() === 0,
      isNow: i === window.now_index,
      isPast: i <= window.now_index,
    };
    for (const s of series) {
      const value = s.values[i];
      if (value != null) applyValue(point, s, value);
    }
    points.push(point);
  }
  return points;
}

// Ulkoiset vaikuttimet — building-level. Fixed, universal physical quantities (not
// equipment-type-varying KB knowledge, unlike the recipe ports below), so branching on the
// literal signal name is the right call here — see root CLAUDE.md's "Knowledge Base First":
// the KB governs what's equipment-specific, not stable concepts like "outdoor temperature".
export function buildEnvironmentPoints(
  report: OperationalLoopReport,
): OperationalLoopPoint[] {
  return seriesToPoints(report.window, report.external, (point, s, value) => {
    if (s.name === "electricity_price_eur_kwh") {
      point.spotPriceCtPerKwh = value * 100; // EUR/kWh -> snt/kWh, matching the chart's own unit
    } else if (s.name === "outdoor_temperature_c") {
      point.outdoorTempC = value;
    } else if (s.name === "solar_radiation_wm2") {
      point.solarRadiationWm2 = value;
    }
  });
}

// Tilojen tarve — one call per zone the equipment serves (KB zone connection `zone_publishes` —
// co2 drives co2_boost, occupancy drives occupancy setback/pre-occupancy flush, temperature
// drives supply-temperature-limit calibration). Same fixed-name reasoning as the environment
// points above — zone signal names are stable across every zone type.
//
// Käyttöaste has two real shapes, never folded into each other: a zone with an `occupancy_count`
// sensor reports real headcount (raw persons — the frontend never reads `nominal_capacity_persons`
// or otherwise turns this into a 0–100 percentage); a zone with only the `occupancy` on/off signal
// has no headcount to report, so it gets presence instead — occupancyModeOf still distinguishes
// that from a fabricated 0–100 käyttöaste. `report.internal[].series` always
// carries a slot for every internal signal name (the backend's fixed `_INTERNAL_SIGNAL_NAMES`),
// all-null when the zone genuinely has no such sensor — so "has occupancy_count" means "has at
// least one real value", not merely "the name appears".
export function buildZonePoints(
  report: OperationalLoopReport,
  series: OperationalLoopSeries[],
): OperationalLoopPoint[] {
  const hasHeadcount = series.some(
    (s) => s.name === "occupancy_count" && s.values.some((v) => v != null),
  );
  return seriesToPoints(report.window, series, (point, s, value) => {
    if (s.name === "co2") {
      point.co2Index = value; // raw ppm despite the field name — kept on the loop point for
      // verification / cursor cards, not plotted on DemandChart (KPI scale only)
    } else if (s.name === "occupancy_count") {
      point.occupancyHeadcount = value;
    } else if (s.name === "occupancy" && !hasHeadcount) {
      point.occupancyPresence = value > 0 ? 100 : 0;
    } else if (s.name === "temperature") {
      point.zoneTemperatureC = value;
    }
  });
}

export type OccupancyMode = "headcount" | "presence" | "index";

/** Which of a zone's own points carry real headcount/presence. "index" is the
 *  illustrative-fallback shape (mock data, or the equipment-level fallback when a device
 *  has no zone link at all). */
export function occupancyModeOf(points: OperationalLoopPoint[]): OccupancyMode {
  if (points.some((p) => p.occupancyHeadcount != null)) return "headcount";
  if (points.some((p) => p.occupancyPresence != null)) return "presence";
  return "index";
}

/** DemandChart's tooltip formatter: the chart plots the shared −100…+100 KPI scale, never
 *  the raw persons / ppm / °C the score was resolved from. */
export function formatDemandTooltipValue(_dataKey: string, value: number): string {
  return `${Math.round(value)} %`;
}

// Optimointiresepti — equipment-level. `report.recipe` is empty when this equipment has no real
// recipe history yet (OperationalLoopSection shows unavailable, never a demo curve).
//
// Which chart axis a series lands on is a units decision (`%` -> the power/valve axis, `°C` ->
// the temperature axis), not equipment-type knowledge — those two axes are RecipeChart's own
// fixed presentation shape, unrelated to which specific equipment type is selected. Percent
// recipes are offset-native (0 = hold); the chart domain is auto around that, not a 0–100
// absolute fan speed. Which real signal name maps to which axis, and its display label, both
// come from the backend's KB-sourced `label`/`role` (services/building/.../operational_loop.py,
// ultimately KB `writable`/`baseline_of` — docs/spec/knowledge-base.md § Interface ports and
// signal roles) — never a hardcoded name or equipment-type lookup here. An equipment type with
// only one writable port and no paired sensor (e.g. a heating-curve offset) simply never
// populates the measured/baseline fields — no fabricated reference value.
export function buildRecipePoints(
  report: OperationalLoopReport,
): OperationalLoopPoint[] {
  return seriesToPoints(report.window, report.recipe, (point, s, value) => {
    if (s.role === "optimized") {
      if (s.unit === "%") point.fanPowerOptimizedPct = value;
      else if (s.unit === "°C") point.supplyTempOptimizedC = value;
    } else if (s.role === "measured") {
      if (s.unit === "%") point.fanPowerMeasuredPct = value;
      else if (s.unit === "°C") point.supplyTempMeasuredC = value;
    } else if (s.role === "baseline") {
      if (s.unit === "%") point.fanPowerBaselinePct = value;
      else if (s.unit === "°C") point.supplyTempBaselineC = value;
    }
  });
}

// ─── Verification overlays ────────────────────────────────────────────────────

export type ApplyMarker = {
  /** x-position on the shared step grid. */
  key: number;
  label: string;
};

/** Where each applied recipe landed on the loop's step grid — the vertical markers every loop
 *  chart draws so a recipe's effect can be read against its cause. A recipe already in force as
 *  the grid starts (applied before the anchor) gets no marker: it didn't land inside the view. */
export function applyMarkersOf(report: OperationalLoopReport): ApplyMarker[] {
  const anchorMs = new Date(report.window.anchor).getTime();
  return report.recipe_windows
    .filter(
      (w) =>
        w.applied_at != null && new Date(w.applied_at).getTime() >= anchorMs,
    )
    .map((w) => ({
      key: w.start_index,
      label: new Date(w.applied_at as string).toLocaleTimeString("fi-FI", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: HELSINKI_TIME_ZONE,
      }),
    }));
}

export type DemandConstraint = {
  /** Which DemandChart series/axis the limit belongs to. */
  signal: "co2" | "temperature";
  threshold: number;
  comparator: "<=" | ">=";
  label: string;
};

/** The strategy bounds as horizontal reference lines on Sisäiset vaikuttimet (verification
 *  question 2) — only the signals that chart draws, de-duplicated on signal + threshold. */
export function demandConstraintsOf(
  constraints: OperationalLoopConstraint[],
): DemandConstraint[] {
  const seen = new Map<string, DemandConstraint>();
  for (const c of constraints) {
    if (c.signal !== "co2" && c.signal !== "temperature") continue;
    const id = `${c.signal}:${c.comparator}:${c.threshold}`;
    if (seen.has(id)) continue;
    const unit = c.unit ? ` ${c.unit}` : "";
    seen.set(id, {
      signal: c.signal,
      threshold: c.threshold,
      comparator: c.comparator,
      label: `${SIGNAL_LABELS[c.signal] ?? c.signal} ${c.comparator === "<=" ? "enint." : "väh."} ${c.threshold}${unit}`,
    });
  }
  return [...seen.values()];
}

// ─── Yhteisvaikutus ───────────────────────────────────────────────────────────

/** Reshape the building service's already-computed fan-electrical series. No affinity math
 *  here — `report.impact` is empty when this equipment has no % port. `elecKw` /
 *  `totalBaselineKw` still carry percent of scheduled (offset-native) or nameplate
 *  (leftover absolute speed) fan power despite the field names. `elecKwh` /
 *  `totalBaselineKwh` are the parallel kWh reading — populated only when the backend actually
 *  sent `fan_electrical_kwh` (absolute speed + nameplate configured), never computed here. */
export function buildImpactPoints(
  report: OperationalLoopReport,
): OperationalLoopPoint[] {
  return seriesToPoints(report.window, report.impact ?? [], (point, s, value) => {
    if (s.name === "fan_electrical_pct") {
      if (s.role === "baseline") {
        point.totalBaselineKw = value;
      } else {
        point.elecKw = value;
        point.totalOptimizedKw = value;
      }
    } else if (s.name === "fan_electrical_kwh") {
      if (s.role === "baseline") {
        point.totalBaselineKwh = value;
      } else {
        point.elecKwh = value;
      }
    }
  });
}

/** Whether the backend sent the kWh pair for Yhteisvaikutus — the card adds a kWh reading only
 *  then, never fabricating one from % and an assumed nameplate. */
export function hasImpactKwh(report: OperationalLoopReport | null): boolean {
  return !!report?.impact?.some((s) => s.name === "fan_electrical_kwh");
}

// Yhteisvaikutus terms this loop cannot estimate yet — FI display names for the backend's
// contract-declared vocabulary (`OperationalLoopReport.not_computable_terms`,
// services/building/.../operational_loop.py `_ALWAYS_NOT_COMPUTABLE_TERMS`). A name missing
// from this map still shows (falls back to the raw term) rather than silently dropping it —
// the backend's list is the source of truth, not this map's completeness.
const NOT_COMPUTABLE_TERM_LABELS: Record<string, string> = {
  heating: "lämmitys",
  cooling: "jäähdytys",
  heat_recovery: "LTO",
  fan_electrical: "puhallinsähkö",
};

/** FI display names for `report.not_computable_terms`, in the backend's own order — the terms
 *  Yhteisvaikutus names as *ei vielä laskettavissa* instead of drawing an illustrative curve. */
export function notComputableLabelsOf(
  report: OperationalLoopReport | null,
): string[] {
  return (report?.not_computable_terms ?? []).map(
    (term) => NOT_COMPUTABLE_TERM_LABELS[term] ?? term,
  );
}

/** "a, b ja c" — Finnish list join for the not-computable-terms sentence. */
export function joinFi(items: string[]): string {
  if (items.length <= 1) return items.join("");
  return `${items.slice(0, -1).join(", ")} ja ${items[items.length - 1]}`;
}

/** Time grid only — DemandChart needs the shared x-axis even when it plots `zone.kpi`. */
export function emptyLoopPoints(
  report: OperationalLoopReport,
): OperationalLoopPoint[] {
  return seriesToPoints(report.window, [], () => {});
}

export type AlignedZoneKpiCurveRow = {
  key: number;
  occupancy: number | null;
  air_quality: number | null;
  temperature: number | null;
  /** `true` when this step's `occupancy` value is the weekly-pattern estimate rather than a
   *  real reading — the backend's own `pattern_filled` marker (task #1884), never inferred here
   *  from `key`/`isPast`. Always `false` for air_quality/temperature: the backend never
   *  pattern-fills those (task #1884) — their future stays an honest hole instead. */
  occupancyPatternFilled: boolean;
};

/** Backend-bucketed KPI series already on the loop grid (including weekly-pattern future). */
export function kpiCurveFromSeries(
  series: OperationalLoopSeries[],
): AlignedZoneKpiCurveRow[] {
  const occupancy = series.find(
    (s) => s.name === PERSISTED_NAME_FOR_ZONE_KPI.occupancy,
  );
  const airQuality = series.find(
    (s) => s.name === PERSISTED_NAME_FOR_ZONE_KPI.air_quality,
  );
  const temperature = series.find(
    (s) => s.name === PERSISTED_NAME_FOR_ZONE_KPI.temperature,
  );
  const length = Math.max(
    occupancy?.values.length ?? 0,
    airQuality?.values.length ?? 0,
    temperature?.values.length ?? 0,
  );
  const rows: AlignedZoneKpiCurveRow[] = [];
  for (let key = 0; key < length; key++) {
    const occupancyValue = occupancy?.values[key] ?? null;
    const airQualityValue = airQuality?.values[key] ?? null;
    const temperatureValue = temperature?.values[key] ?? null;
    if (
      occupancyValue == null &&
      airQualityValue == null &&
      temperatureValue == null
    ) {
      continue;
    }
    rows.push({
      key,
      occupancy: occupancyValue,
      air_quality: airQualityValue,
      temperature: temperatureValue,
      occupancyPatternFilled: occupancy?.pattern_filled?.[key] ?? false,
    });
  }
  return rows;
}

export type RecipeAxisLabels = {
  percentLabel: string;
  tempLabel: string;
};

const DEFAULT_RECIPE_AXIS_LABELS: RecipeAxisLabels = {
  percentLabel: "Ohjaussuure",
  tempLabel: "Lämpötilan offset",
};

// The chart-axis titles RecipeChart shows, read from the same KB-sourced `label` the data
// itself carries — "names should be provided together with data" — instead of a separate
// per-equipment-type label lookup table on the frontend. Falls back to a generic, type-neutral
// label only when there's no real "optimized"-role series to name the axis from (illustrative
// data, or an equipment type with no writable port at all).
export function recipeAxisLabels(
  report: OperationalLoopReport | null,
): RecipeAxisLabels {
  const optimized = report?.recipe.filter((s) => s.role === "optimized") ?? [];
  const percent = optimized.find((s) => s.unit === "%");
  const temp = optimized.find((s) => s.unit === "°C");
  return {
    percentLabel: percent?.label ?? DEFAULT_RECIPE_AXIS_LABELS.percentLabel,
    tempLabel: temp?.label ?? DEFAULT_RECIPE_AXIS_LABELS.tempLabel,
  };
}

/** Which recipe-chart axes the recipes in view actually wrote to. `null` when there are no
 *  windows to judge by (illustrative data), meaning "show both".
 *
 *  Window `targets` keys are recipe parameter ids. After a port rename those keys can miss the
 *  live series (leftover `supply_fan_speed_override` vs `ventilation_level_offset`), and a
 *  persist curve can exist without a matching applied window (mechanism 3 writes the % offset
 *  every tick; mechanism 1 may be the only applied recipe). Either case still has a real %
 *  series — hide the axis only when nothing of that unit was actually written. */
export function writtenRecipeAxes(
  report: OperationalLoopReport | null,
): { percent: boolean; temp: boolean } | null {
  if (!report || report.recipe_windows.length === 0) return null;
  const unitOf = new Map(report.recipe.map((s) => [s.name, s.unit]));
  const units = new Set(
    report.recipe_windows.flatMap((w) =>
      Object.keys(w.targets).map((name) => unitOf.get(name) ?? null),
    ),
  );
  for (const s of report.recipe) {
    if (s.role !== "optimized" || (s.unit !== "%" && s.unit !== "°C")) continue;
    if (s.values.some((v) => v != null)) units.add(s.unit);
  }
  return { percent: units.has("%"), temp: units.has("°C") };
}

